import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, AlertCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { renderField } from "@/lib/myapp/field-renderer";
import {
  evaluateFormula as evaluateFormulaFn,
  evaluateConcatenation as evaluateConcatenationFn,
  buildFieldValueMap,
} from "@/lib/myapp/formula-evaluator";
import {
  evaluateCalculatedField,
  type CalculatedFieldDisplay,
} from "@/lib/myapp/tsql-ir";
import {
  myappPublicApi,
  type PublicTableResponse,
  type PublicTableField,
  type PublicLinkedOption,
} from "@/services/api/myapp";
import type { FormField, LinkedOption } from "@/types/myapp";
import { getDeviceToken } from "@/lib/myapp/device-token";
import { useRecaptcha } from "@/lib/myapp/recaptcha";
import { fieldDecimalPrecisionError } from "@/lib/myapp/numeric-input-step";

export const Route = createFileRoute("/forms/$slug/respond/")({
  component: FormRespondPage,
});

// Direct data-ENTRY guard for a decimal/money field's value on the public
// forms page — the gate-review counterpart of submit.tsx's identically-
// behaved guard for the standalone record form (this page's unguarded
// `parseFloat(String(value))` in handleSubmit would otherwise let an
// over-precision value reach the backend and get silently rounded by the
// float/JSON write path). See `fieldDecimalPrecisionError` in
// numeric-input-step.ts for the shared ceiling logic (decimal widens with
// `decimal_places`, money stays fixed at 4) — kept there, not duplicated
// here, so this file can export only the page component (react-refresh
// lint rule) while still sharing the exact same validation as every other
// entry surface.
//
// Note: the public field schema (PublicTableField) doesn't expose
// `decimal_places` yet (see toFormField above — hardcoded null), so a
// decimal field on this page effectively floors at DEFAULT_DECIMAL_PLACES
// (4) regardless of what the field's real setting is; it never admits more
// than the backend's own storage floor, it just can't yet widen past it
// here the way grid.tsx/submit.tsx can.
const decimalPrecisionError = fieldDecimalPrecisionError;

/**
 * Adapt a PublicTableField (from the public API) into a FormField shape
 * so it can be passed to the shared renderField() function.
 */
function toFormField(pf: PublicTableField): FormField {
  return {
    id: pf.id,
    table_def_id: "",
    name: pf.name,
    column_name: pf.name, // public API uses name as column identifier
    original_name: null,
    field_type_id: "",
    field_type: {
      id: "",
      code: pf.field_type_code,
      name: pf.field_type_name,
      sql_type: "",
      supports_range: false,
      sort_order: 0,
    },
    description: pf.description,
    is_required: pf.is_required,
    is_unique: false,
    default_value: pf.default_value,
    sort_order: pf.sort_order,
    max_length: null,
    min_value: pf.min_value,
    max_value: pf.max_value,
    linked_table_id: pf.linked_table_id,
    linked_table: null,
    linked_display_field_id: null,
    source_link_field_id: pf.source_link_field_id ?? null,
    lookup_field_id: null,
    calculation_formula: pf.calculation_formula ?? null,
    calculation_spec_version: pf.calculation_spec_version,
    calculation_ir: pf.calculation_ir ?? null,
    concatenation_template: pf.concatenation_template ?? null,
    // The public form API does not yet expose OptionsConfig, so an options
    // field degrades to a plain text input here (the backend still validates
    // the submitted value against the allowed list). Exposing it on the
    // public schema is a follow-up.
    options_config: null,
    show_total: false,
    decimal_places: null,
    is_hidden: false,
    is_inherited: false,
    inherit_from_field_id: null,
    source_field_id: null,
    is_disconnected: false,
    disconnected_reason: null,
    is_auto_numbered: pf.is_readonly ?? false,
    cascade_parent_field_id: null,
    cascade_filter_column: null,
    linked_display_field_name: null,
    rollup_source_field_id: null,
    rollup_function: null,
    kanban_mode: null,
  };
}

function FormRespondPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const { executeRecaptcha } = useRecaptcha();

  const [table, setTable] = useState<PublicTableResponse | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  // Honeypot — hidden field that bots fill in
  const [honeypotValue, setHoneypotValue] = useState<string>("");

  // Track which fields have been touched for validation
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Linked field options (keyed by field id)
  const [linkedOptions, setLinkedOptions] = useState<
    Record<string, LinkedOption[]>
  >({});
  const [loadingLinkedOptions, setLoadingLinkedOptions] = useState<
    Record<string, boolean>
  >({});

  // Progress calculation
  const [progress, setProgress] = useState(0);

  // Prevent double-fetching in React strict mode
  const fetchedRef = useRef(false);

  const fetchLinkedOptions = useCallback(
    async (field: PublicTableField) => {
      if (!field.linked_table_id) return;
      setLoadingLinkedOptions((prev) => ({ ...prev, [field.id]: true }));
      try {
        const options: PublicLinkedOption[] =
          await myappPublicApi.getLinkedOptions(slug, field.id);
        setLinkedOptions((prev) => ({ ...prev, [field.id]: options }));
      } catch (err) {
        console.error("Failed to load linked options:", err);
      } finally {
        setLoadingLinkedOptions((prev) => ({ ...prev, [field.id]: false }));
      }
    },
    [slug],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const tableData = await myappPublicApi.getTable(slug);
      setTable(tableData);

      // Convert to FormField shape for rendering
      const formFields = tableData.fields.map(toFormField);
      setFields(formFields);

      // Initialize form data with defaults
      const initialData: Record<string, unknown> = {};
      tableData.fields.forEach((field) => {
        const defaultValue = field.default_value;
        const fieldType = field.field_type_code;

        // SQL function defaults — approximate on client for UX
        const isSqlFunction =
          defaultValue &&
          (defaultValue.toUpperCase() === "GETDATE()" ||
            defaultValue.toUpperCase().startsWith("DATEADD(") ||
            defaultValue.toUpperCase() === "NEWID()");

        if (fieldType === "bit") {
          initialData[field.name] =
            defaultValue === "1" || defaultValue === "true";
        } else if (isSqlFunction) {
          if (defaultValue?.toUpperCase() === "GETDATE()") {
            initialData[field.name] = new Date().toISOString().split("T")[0];
          } else if (defaultValue?.toUpperCase().startsWith("DATEADD(")) {
            const match = defaultValue.match(
              /DATEADD\(DAY,(-?\d+),GETDATE\(\)\)/i,
            );
            if (match) {
              const days = parseInt(match[1]);
              const date = new Date();
              date.setDate(date.getDate() + days);
              initialData[field.name] = date.toISOString().split("T")[0];
            } else {
              initialData[field.name] = "";
            }
          } else {
            initialData[field.name] = "";
          }
        } else {
          initialData[field.name] = defaultValue || "";
        }
      });
      setFormData(initialData);

      // Fetch linked options for link fields
      tableData.fields.forEach((field) => {
        if (field.field_type_code === "link" && field.linked_table_id) {
          fetchLinkedOptions(field);
        }
      });

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load form");
    } finally {
      setLoading(false);
    }
  }, [slug, fetchLinkedOptions]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchData();
  }, [fetchData]);

  // Calculate progress when form data changes
  useEffect(() => {
    if (fields.length === 0) return;

    const requiredFields = fields.filter((f) => f.is_required);
    if (requiredFields.length === 0) {
      setProgress(100);
      return;
    }

    const filledRequiredFields = requiredFields.filter((field) => {
      const value = formData[field.column_name];
      if (field.field_type?.code === "bit") return true;
      return value !== "" && value !== null && value !== undefined;
    });

    setProgress(
      Math.round((filledRequiredFields.length / requiredFields.length) * 100),
    );
  }, [fields, formData]);

  const handleFieldChange = (columnName: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [columnName]: value }));
    setTouchedFields((prev) => new Set(prev).add(columnName));
  };

  const handleFieldBlur = (columnName: string) => {
    setTouchedFields((prev) => new Set(prev).add(columnName));
  };

  const isFieldInvalid = (field: FormField): boolean => {
    if (!touchedFields.has(field.column_name)) return false;
    const value = formData[field.column_name];
    if (decimalPrecisionError(field, value)) return true;
    if (!field.is_required) return false;
    if (field.field_type?.code === "bit") return false;
    return value === "" || value === null || value === undefined;
  };

  const handleSubmit = async () => {
    if (!table || fields.length === 0) return;

    // Mark all fields as touched
    const allFieldNames = fields.map((f) => f.column_name);
    setTouchedFields(new Set(allFieldNames));

    // Validate required fields
    const missingRequired = fields.filter((field) => {
      if (!field.is_required) return false;
      const value = formData[field.column_name];
      if (field.field_type?.code === "bit") return false;
      return value === "" || value === null || value === undefined;
    });

    if (missingRequired.length > 0) {
      setError(
        `Please fill in all required fields: ${missingRequired.map((f) => f.name).join(", ")}`,
      );
      return;
    }

    // Pre-submit decimal/money precision check — a hard gate alongside the
    // live per-field red-border state above (isFieldInvalid), so an
    // over-precision value can never reach the backend from this public
    // form. Mirrors submit.tsx's equivalent pre-submit check.
    const precisionIssues = fields
      .map((field) => ({
        field,
        message: decimalPrecisionError(field, formData[field.column_name]),
      }))
      .filter((x): x is { field: FormField; message: string } => x.message !== null);

    if (precisionIssues.length > 0) {
      setError(
        `Fix decimal precision before submitting: ${precisionIssues
          .map((x) => `${x.field.name} (${x.message})`)
          .join(", ")}`,
      );
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Build submission data with correct types
      const submitData: Record<string, unknown> = {};
      fields.forEach((field) => {
        const value = formData[field.column_name];
        const fieldType = field.field_type?.code;

        // v2 (T-SQL) calc columns are OMITTED entirely, never sent as null —
        // SQL Server is the ONLY thing that computes a v2 stored value,
        // inside the same statement that writes the row. Sending even a
        // null would make the browser a second writer of that column,
        // exactly the parity risk this migration removes (mirrors
        // submit.tsx / HeaderLineForm.tsx / grid.tsx).
        if (fieldType === "calculation" && field.calculation_spec_version === 2) {
          return;
        }

        if (value === "" || value === null || value === undefined) {
          if (!field.is_required) {
            submitData[field.column_name] = null;
          }
          return;
        }

        switch (fieldType) {
          case "int":
          case "bigint":
            submitData[field.column_name] = Number(value);
            break;
          case "decimal":
          case "money":
            submitData[field.column_name] = parseFloat(String(value));
            break;
          case "bit":
            submitData[field.column_name] = Boolean(value);
            break;
          default:
            submitData[field.column_name] = value;
        }
      });

      // Get reCAPTCHA token if required
      let recaptchaToken: string | null = null;
      if (table.require_captcha) {
        recaptchaToken = await executeRecaptcha("submit_form");
      }

      const deviceToken = getDeviceToken();
      const result = await myappPublicApi.submit(
        slug,
        submitData,
        deviceToken,
        honeypotValue || null,
        recaptchaToken,
      );

      navigate({
        to: "/forms/$slug/respond/success",
        params: { slug },
        search: {
          message: result.confirmation_message || undefined,
          refNumber: result.auto_number_value || undefined,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const renderFieldInput = (field: FormField) => {
    // v2 (T-SQL) calc fields are never sent through evaluateFormulaFn — that
    // evaluator only understands the legacy `+ - * /` mini-language. A v2
    // field's live preview comes from the compiled IR (evaluateIr), shipped
    // as inert JSON on the public field list; falling back to the legacy
    // evaluator here would silently mis-parse T-SQL syntax as arithmetic.
    const calcDisplay: CalculatedFieldDisplay | undefined =
      field.field_type?.code !== "calculation"
        ? undefined
        : field.calculation_spec_version === 2
          ? evaluateCalculatedField(
              field.calculation_ir,
              buildFieldValueMap(fields, formData),
            )
          : { calculatedValue: evaluateFormulaFn(field.calculation_formula, fields, formData), calculationComputedOnSave: false };

    return renderField(field, {
      value: formData[field.column_name],
      onChange: (val) => handleFieldChange(field.column_name, val),
      onBlur: () => handleFieldBlur(field.column_name),
      linkedOptions: linkedOptions[field.id],
      loadingLinkedOptions: loadingLinkedOptions[field.id],
      isInvalid: isFieldInvalid(field),
      showBitLabel: true,
      ...calcDisplay,
      concatenatedValue:
        field.field_type?.code === "concatenation"
          ? evaluateConcatenationFn(
              field.concatenation_template,
              fields,
              formData,
            )
          : undefined,
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state (no form)
  if (error && !table) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Unable to load</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button
            onClick={() => {
              fetchedRef.current = false;
              fetchData();
            }}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Form not found
  if (!table) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Not found</h2>
          <p className="text-muted-foreground">
            This form doesn&apos;t exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const requiredFieldCount = fields.filter((f) => f.is_required).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="h-1 bg-muted">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 pt-12">
        {/* Form Header Card */}
        <div className="bg-card rounded-lg border shadow-sm overflow-hidden mb-6">
          <div className="h-2 bg-primary" />
          <div className="p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {table.name}
            </h1>
            {table.description && (
              <p className="text-muted-foreground">{table.description}</p>
            )}
            {requiredFieldCount > 0 && (
              <p className="text-sm text-muted-foreground mt-4">
                <span className="text-destructive">*</span> Required fields
              </p>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="bg-card rounded-lg border shadow-sm p-6 transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/50 animate-[formFieldEnter_0.3s_ease-out_forwards]"
              style={{ animationDelay: `${index * 50}ms`, opacity: 0 }}
            >
              <div className="mb-4">
                <Label
                  htmlFor={field.column_name}
                  className="text-base font-medium"
                >
                  {field.name}
                  {field.is_required && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                {field.description && field.field_type?.code !== "bit" && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {field.description}
                  </p>
                )}
              </div>
              {renderFieldInput(field)}
              {isFieldInvalid(field) && (
                <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  This field is required
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Honeypot — hidden from real users, bots will fill it */}
        {table.honeypot_enabled && (
          <div
            aria-hidden="true"
            tabIndex={-1}
            style={{
              position: "absolute",
              left: "-9999px",
              top: "-9999px",
              opacity: 0,
              height: 0,
              overflow: "hidden",
            }}
          >
            <label htmlFor="website_url">Website</label>
            <input
              id="website_url"
              name="website_url"
              type="text"
              autoComplete="off"
              tabIndex={-1}
              value={honeypotValue}
              onChange={(e) => setHoneypotValue(e.target.value)}
            />
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-8 flex justify-between items-center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={submitting}
            className="gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Submit
              </>
            )}
          </Button>
          <p className="text-sm text-muted-foreground">{progress}% complete</p>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t text-center">
          <p className="text-sm text-muted-foreground">
            Powered by <span className="font-medium">Lara MyApp</span>
          </p>
        </div>
      </div>
    </div>
  );
}
